import React, { HTMLAttributes, useContext } from 'react'
import Loadable from 'react-loadable'
import classNames from 'classnames'
import {
  ConversationDescriptor,
  FirebaseConversationDescriptor,
  LocalConversationDescriptor
} from '#src/hooks/useConversation'
import type { ChatContainerProps } from '@deal/chat-firebase'
import { IdentityContext } from '#src/containers/Identity'
import { useStartExpertConsumerConversationMutation } from '../../mutations/StartExpertConsumerConversation.generated'
import { useChatExpertByExpertIdQuery } from './ChatExpertByExpertId.generated'
import { Link } from 'react-router-dom'
import { FullBlockLoader, CircleLoader } from '@deal/components'
import EmailIcon from './email.svg'
import PhoneIcon from './phone.svg'
import { UserAgentContext } from '#src/containers/UserAgent'
import EmptyState from './EmptyState'
import ExpertRatingAvatar from '#src/components/ExpertRatingAvatar'
import DeactivatedExpertChatBanner from '#src/components/DeactivatedExpertChatBanner'
import aboveBreakpoint from '#src/hocs/aboveBreakpoint'
import styles from './styles.css'

const LazyLoadedChat = Loadable({
  loader: () => import(/* webpackChunkName: "chat-sidebar-conversation" */ '@deal/chat-firebase'),
  render: ({ Chat }, props: ChatContainerProps) => {
    return <Chat {...props} />
  },
  loading: () => <FullBlockLoader loader={CircleLoader} />
})

interface ChatSidebarProps {
  // Defaults to "Loading"
  conversation?: ConversationDescriptor
  style?: HTMLAttributes<HTMLDivElement>['style']
  className?: string
}

// TODO: Consolidate business logic between ChatSidebar & ChatTab
const ChatSidebar: React.FC<ChatSidebarProps> = ({ conversation, className, style, ...props }) => {
  // If no ConversationDescriptor is provided, but we are rendering the sidebar, we assume
  //  that it is because the data necessary to fetch the ConversationDescriptor is loading.
  if (!conversation) {
    conversation = { type: 'Loading' }
  }

  const { myself } = useContext(IdentityContext)
  const { data } = useChatExpertByExpertIdQuery({
    variables: {
      businessUserId: (conversation as FirebaseConversationDescriptor | LocalConversationDescriptor)
        .expertId
    },
    // Only Firebase and Local conversations havve an expertId to query by. If the conversation
    //  is loading or no experts were available, we skip this query.
    skip: conversation.type !== 'FirebaseConversation' && conversation.type !== 'LocalConversation'
  })
  const [
    startExpertConsumerConversation,
    startExpertConsumerConversationData
  ] = useStartExpertConsumerConversationMutation()

  const expert = data && data.businessUser
  const expertStats = expert && expert.expertStats
  const expertProfile = expert && expert.expertProfile
  const expertPhone = expert && expert.assignedSmsAddress
  const expertEmail = expert && expert.assignedEmailAddress
  const expertName = expert && expert.displayName

  const handleConversationStarted = () => {
    if (myself && expert) {
      startExpertConsumerConversation({
        variables: {
          input: {
            consumerId: myself.id,
            expertId: expert.id,
            sourceFlow: {
              directExpertChatFlow: {
                expertId: expert.id
              }
            }
          }
        }
      })
    }
  }

  // Use the passed-in conversation ID, if one exists. Otherwise, see if we've created one
  //   via `startExpertConsumerConversation`.
  const conversationId: string | undefined =
    conversation.type === 'FirebaseConversation' && conversation.conversationId
      ? conversation.conversationId
      : startExpertConsumerConversationData.data?.startExpertConsumerConversation.conversation?.id

  // We don't want to show the chat to bots because they index this on every single page
  // which dings our rankings because of duplicate content
  const userAgent = useContext(UserAgentContext)

  if (userAgent?.isBot) {
    return null
  }

  return (
    <div className={classNames(styles.sidebar, className)} style={style}>
      <div className={styles.sidebarContent}>
        {conversation.type === 'Loading' ? (
          <FullBlockLoader loader={CircleLoader} />
        ) : conversation.type === 'NoExpertsAvailable' ? (
          <EmptyState />
        ) : (
          <>
            {expert && expertProfile && (
              <div className={styles.sidebarHeader}>
                <Link
                  to={`/experts/${expert.vanityId || expert.id}`}
                  className={styles.expertAvatarAndRating}
                >
                  <ExpertRatingAvatar
                    expert={expert}
                    size={80}
                    rating={expertStats?.averageRating}
                    ratingClassName={styles.avatarRating}
                  />
                </Link>
                <div className={styles.expertBio}>
                  <div className={styles.expertName}>{expertName!}</div>
                  <div className={styles.expertHeadline}>{expertProfile.headline}</div>
                  {expertEmail && (
                    <div className={styles.expertEmail}>
                      <EmailIcon className={styles.expertContactIcon} />
                      {expertEmail}
                    </div>
                  )}
                  {expertPhone && (
                    <div className={styles.expertPhone}>
                      <PhoneIcon className={styles.expertContactIcon} />
                      {expertPhone}
                    </div>
                  )}
                  <Link
                    to={`/experts/${expert.vanityId || expert.id}`}
                    className={styles.expertProfileLink}
                  >
                    View profile
                  </Link>
                </div>
              </div>
            )}

            <LazyLoadedChat
              conversationId={conversationId}
              guestChatEnabled={!myself || !expert}
              onConversationStarted={handleConversationStarted}
              bottomBanner={() => {
                if (myself && expert && conversationId) {
                  return (
                    <DeactivatedExpertChatBanner
                      consumerName={myself?.user.firstName || ''}
                      expertName={expertName || ''}
                      conversationId={conversationId}
                      expertState={expert.state}
                      placeHolderExpertProfilePhotoUrl={
                        expert.department.placeholderExpert.expertProfile?.profilePhoto?.url
                      }
                      placeHolderExpertName={expert.department?.placeholderExpert?.shortName}
                    />
                  )
                } else {
                  return null
                }
              }}
              recipientIds={expert ? [expert.id] : undefined}
              {...props}
            />
          </>
        )}
      </div>
    </div>
  )
}

export default aboveBreakpoint('lg', ChatSidebar)
